// Dia de Vencimento (issue #40): o dia do mês, cadastrado no Cliente, que
// resolve o vencimento da Fatura.
//
// A regra é **o próximo dia D no calendário depois que o Período de Faturamento
// fecha** — "depois" no sentido estrito: se o período fecha no próprio dia D,
// o vencimento vai para o mês seguinte.
//
//   Ciclo         fecha       D    vence
//   mensal        31/03       10   10/04
//   quinzenal 1ª  15/03       25   25/03
//   quinzenal 1ª  15/03        5   05/04
//   quinzenal 2ª  31/03       25   25/04
//
// Os dias ficam em `Cliente.diasVencimento`, **na ordem dos períodos do
// Ciclo**: um valor nos Ciclos mensal e customizado, dois no quinzenal (um por
// quinzena). Lista vazia = não configurado, e a Fatura abre o vencimento em
// branco para o operador digitar.

import { isDataISO, type DataISO } from "./datas.ts";
import type { CicloFechamento, Cliente } from "./snapshot.ts";

export class ErroVencimento extends Error {}

/** Quantos Dias de Vencimento o Ciclo comporta. */
export function quantosDiasVencimento(ciclo: CicloFechamento): number {
  return ciclo.tipo === "quinzenal" ? 2 : 1;
}

/** Último dia do mês (1-12) de um ano. */
function ultimoDiaDoMes(ano: number, mes: number): number {
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate();
}

function iso(ano: number, mes: number, dia: number): DataISO {
  const mm = String(mes).padStart(2, "0");
  const dd = String(dia).padStart(2, "0");
  return `${ano}-${mm}-${dd}`;
}

/**
 * O próximo dia `dia` do calendário **estritamente depois** de
 * `fimDoPeriodo`.
 *
 * Um `dia` maior que o último dia do mês alvo cai no último dia dele — dia 31
 * em abril vence em 30/04. É a convenção de cobrança usual, e a alternativa
 * (pular para o mês seguinte) atrasaria a cobrança em um mês inteiro.
 */
export function proximoVencimento(fimDoPeriodo: DataISO, dia: number): DataISO {
  if (!isDataISO(fimDoPeriodo)) {
    throw new ErroVencimento("Data de fim do período inválida.");
  }
  if (!Number.isInteger(dia) || dia < 1 || dia > 31) {
    throw new ErroVencimento("O dia de vencimento deve ser um número de 1 a 31.");
  }
  const [ano, mes] = fimDoPeriodo.split("-").map(Number) as [number, number, number];

  // tenta o mês do próprio fim do período; se não passar dele, vai pro seguinte
  for (const [a, m] of [
    [ano, mes],
    mes === 12 ? [ano + 1, 1] : [ano, mes + 1],
  ] as [number, number][]) {
    const candidato = iso(a, m, Math.min(dia, ultimoDiaDoMes(a, m)));
    if (candidato > fimDoPeriodo) return candidato;
  }
  /* c8 ignore next */
  throw new ErroVencimento("Não foi possível resolver o vencimento.");
}

/**
 * O Dia de Vencimento do Cliente para um período, ou `null` se não houver.
 *
 * `indicePeriodo` é a posição do período dentro do Ciclo: 0 nos Ciclos mensal e
 * customizado, 0 (1ª quinzena) ou 1 (2ª quinzena) no quinzenal.
 */
export function diaVencimentoDe(
  cliente: Pick<Cliente, "cicloFechamento" | "diasVencimento">,
  indicePeriodo: number,
): number | null {
  const dias = cliente.diasVencimento;
  if (indicePeriodo < 0 || indicePeriodo >= quantosDiasVencimento(cliente.cicloFechamento)) {
    return null;
  }
  return dias[indicePeriodo] ?? null;
}

/**
 * Em que posição do Ciclo cai um período que fecha em `fim`.
 *
 * Só o Ciclo quinzenal tem mais de uma: a 1ª quinzena fecha no dia 15, a 2ª no
 * último dia do mês. Um período quinzenal que feche noutro dia (ajustado à mão
 * na geração da Fatura) conta como 2ª — é a que fecha o mês.
 */
export function indiceDoPeriodo(ciclo: CicloFechamento, fim: DataISO): number {
  if (ciclo.tipo !== "quinzenal") return 0;
  const dia = Number(fim.slice(8, 10));
  return dia <= 15 ? 0 : 1;
}

/**
 * O vencimento sugerido para uma Fatura do Cliente cujo período fecha em `fim`.
 * `null` quando o Cliente não tem Dia de Vencimento cadastrado para aquele
 * período — a tela abre o campo em branco.
 */
export function vencimentoSugerido(
  cliente: Pick<Cliente, "cicloFechamento" | "diasVencimento">,
  fim: DataISO,
): DataISO | null {
  const dia = diaVencimentoDe(cliente, indiceDoPeriodo(cliente.cicloFechamento, fim));
  return dia == null ? null : proximoVencimento(fim, dia);
}

/**
 * Valida a lista de Dias de Vencimento contra o Ciclo. Devolve a lista aparada:
 * vazia (não configurado) ou com exatamente a quantidade que o Ciclo comporta.
 */
export function validarDiasVencimento(
  dias: readonly number[],
  ciclo: CicloFechamento,
): number[] {
  const limpos = dias.filter((d) => d != null && !Number.isNaN(d));
  if (limpos.length === 0) return [];
  const esperado = quantosDiasVencimento(ciclo);
  if (limpos.length !== esperado) {
    throw new ErroVencimento(
      esperado === 1
        ? "Informe um dia de vencimento, ou deixe em branco."
        : "No ciclo quinzenal, informe os dois dias de vencimento, ou deixe os dois em branco.",
    );
  }
  for (const d of limpos) {
    if (!Number.isInteger(d) || d < 1 || d > 31) {
      throw new ErroVencimento("O dia de vencimento deve ser um número de 1 a 31.");
    }
  }
  return limpos;
}
